import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { injectIntl } from "react-intl";
import { getGameByCode, formatGameLoadError } from "../api/game";
import { setGame, setGroup } from "../redux/actions";
import { ROUTES } from "../utils/constants";
import { getPlayablePages } from "../components/EscapeRoomRenderer";
import { useMedia } from "../contexts/MediaContext";
import { clearItem, getGroupTable } from "../utils/helpers";
import { clearErPlaySession, loadErPlaySession } from "../utils/erPlaySessionStorage";
import { groupLinkedGameId } from "../utils/resetPlaySession";
import LanguageSwitcher from "../components/LanguageSwitcher";
import store from "../redux/store";

function normalizeCode(v) {
	return String(v || "")
		.replace(/\s+/g, "")
		.replace(/[^\d]/g, "")
		.slice(0, 6);
}

function EnterCode({ intl }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const { unlockAllMedia } = useMedia();
	const [code, setCode] = useState("");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const t = (id, values) => intl.formatMessage({ id }, values);

	const canSubmit = useMemo(() => /^\d{6}$/.test(code), [code]);

	useEffect(() => {
		const storedTable = getGroupTable();
		if (!storedTable) clearItem("total");
		Promise.resolve()
			.then(() => unlockAllMedia())
			.catch(() => {});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- unlockAllMedia cambia al desbloquear medios y re-dispararía el efecto en bucle
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(location.search || "");
		const fromUrl = normalizeCode(params.get("code") || params.get("c"));
		if (fromUrl) setCode(fromUrl);
	}, [location.search]);

	const onSubmit = async (e) => {
		e?.preventDefault?.();
		if (busy || !canSubmit) return;
		setBusy(true);
		setError("");
		try {
			const res = await getGameByCode(code);
			const game = res?.status === 200 ? res.data : null;
			if (!game) throw new Error(t("ENTER_CODE.ERROR_INVALID"));
			if (!game.active) {
				throw new Error(t("ENTER_CODE.ERROR_INACTIVE"));
			}
			const session = loadErPlaySession();
			const sameSessionGame = session?.gameId && String(session.gameId) === String(game._id);
			const prevGroupGameId = groupLinkedGameId(store.getState()?.group);
			const groupBelongsToOtherGame = prevGroupGameId && String(prevGroupGameId) !== String(game._id);
			if (!sameSessionGame) {
				clearErPlaySession();
			}
			if (groupBelongsToOtherGame || !sameSessionGame) {
				dispatch(setGroup(null));
				clearItem("groupTable");
				clearItem("total");
				try {
					localStorage.removeItem("erRegisterReturnIndex");
					localStorage.removeItem("erForceRegisterFlow");
				} catch (_) {}
			}
			dispatch(setGame(game));
			const pages = getPlayablePages(game?.escapeRoom?.pages || []);
			const hasEscapeRoom = !!game?.escapeRoom;
			const hasPages = pages.length > 0;
			navigate({ pathname: hasEscapeRoom && hasPages ? ROUTES.ESCAPE_ROOM : ROUTES.REGISTER }, { replace: true });
		} catch (err) {
			const friendly = formatGameLoadError(err, "code");
			setError(friendly || err?.message || t("ENTER_CODE.ERROR_INVALID"));
		} finally {
			setBusy(false);
		}
	};

	return (
		<div
			style={{
				position: "relative",
				minHeight: "100vh",
				display: "grid",
				placeItems: "center",
				padding: "24px 16px",
				background: "radial-gradient(900px 420px at 50% 0%, rgba(109,40,217,0.18) 0%, rgba(255,255,255,0) 60%), linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
			}}
		>
			<div
				style={{
					position: "absolute",
					top: "max(18px, env(safe-area-inset-top, 0px))",
					right: "max(18px, env(safe-area-inset-right, 0px))",
					zIndex: 5,
				}}
			>
				<LanguageSwitcher />
			</div>

			<div
				style={{
					width: "100%",
					maxWidth: 520,
					borderRadius: 22,
					border: "1px solid rgba(15,23,42,0.10)",
					background: "rgba(255,255,255,0.9)",
					boxShadow: "0 30px 90px rgba(15,23,42,0.12)",
					padding: 20,
				}}
			>
				<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
					<div>
						<div style={{ fontSize: 12, fontWeight: 900, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".10em" }}>
							{t("ENTER_CODE.EYEBROW")}
						</div>
						<div style={{ fontSize: 28, fontWeight: 950, color: "#0f172a", letterSpacing: "-0.02em", marginTop: 2 }}>
							{t("ENTER_CODE.TITLE")}
						</div>
					</div>
					<img
						src={`${process.env.PUBLIC_URL || ""}/favicon.svg`}
						alt=""
						width={44}
						height={44}
						decoding="async"
						style={{
							width: 44,
							height: 44,
							borderRadius: 14,
							objectFit: "contain",
							flexShrink: 0,
							background: "#fff",
							border: "1px solid rgba(15,23,42,0.08)",
							boxShadow: "0 10px 28px rgba(15,23,42,0.10)",
						}}
					/>
				</div>

				<form onSubmit={onSubmit} style={{ marginTop: 16 }}>
					<input
						value={code}
						onChange={(e) => setCode(normalizeCode(e?.target?.value))}
						inputMode="numeric"
						autoComplete="one-time-code"
						placeholder="000000"
						style={{
							width: "100%",
							height: 64,
							borderRadius: 18,
							border: "1px solid rgba(15,23,42,0.14)",
							background: "#fff",
							padding: "0 18px",
							fontSize: 34,
							fontWeight: 950,
							letterSpacing: "0.18em",
							textAlign: "center",
							outline: "none",
							boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
						}}
					/>

					<button
						type="submit"
						disabled={!canSubmit || busy}
						style={{
							marginTop: 12,
							width: "100%",
							height: 54,
							borderRadius: 999,
							border: "none",
							cursor: !canSubmit || busy ? "not-allowed" : "pointer",
							background: !canSubmit || busy ? "#e2e8f0" : "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
							color: !canSubmit || busy ? "#64748b" : "#fff",
							fontSize: 16,
							fontWeight: 900,
							letterSpacing: "-0.01em",
							boxShadow: !canSubmit || busy ? "none" : "0 18px 44px rgba(109,40,217,0.24)",
						}}
					>
						{busy ? t("ENTER_CODE.SUBMITTING") : t("ENTER_CODE.SUBMIT")}
					</button>

					{error ? (
						<div style={{ marginTop: 12, borderRadius: 14, border: "1px solid rgba(220,38,38,0.25)", background: "rgba(254,242,242,0.8)", color: "#991b1b", padding: "10px 12px", fontWeight: 800 }}>
							{error}
						</div>
					) : null}
				</form>

				<div style={{ marginTop: 14, color: "#64748b", fontSize: 13, lineHeight: 1.35 }}>
					{t("ENTER_CODE.HINT")}
				</div>
			</div>
		</div>
	);
}

export default injectIntl(EnterCode);
